export const landmarks = [
  { name: "Southwest Florida Intl (RSW)", distance: "~35 miles", icon: "ri-flight-takeoff-line", desc: "Major regional airport serving Fort Myers and Naples with direct flights to major U.S. hubs and seasonal international routes", url: "https://www.flylcpa.com/", schemaType: "Airport" },
  { name: "Naples Pier", distance: "Beachfront", icon: "ri-landscape-line", desc: "Historic 1,000-foot pier on the Gulf of Mexico — iconic sunset viewing, fishing, and dolphin watching destination", url: "https://www.naplesgov.com/", schemaType: "TouristAttraction" },
  { name: "Naples Botanical Garden", distance: "~4 miles", icon: "ri-leaf-line", desc: "170-acre world-class botanical garden with international plant collections and Brazilian garden", url: "https://www.naplesgarden.org/", schemaType: "TouristAttraction" },
  { name: "Fifth Avenue South", distance: "Downtown", icon: "ri-store-line", desc: "Premier shopping and dining district in Old Naples with luxury boutiques, galleries, and acclaimed restaurants", url: "https://www.fifthavenuesouth.com/", schemaType: "TouristAttraction" },
  { name: "Port of Naples / Naples Bay", distance: "Waterfront", icon: "ri-ship-line", desc: "Deepwater marina and yacht harbor serving luxury cruisers and charter fishing vessels on the Gulf", url: "https://www.naplesbayresort.com/", schemaType: "TouristAttraction" },
  { name: "Everglades National Park (Gulf Coast)", distance: "~40 miles", icon: "ri-ancient-gate-line", desc: "UNESCO World Heritage Site — Gulf Coast entrance for kayaking through mangroves and Ten Thousand Islands exploration", url: "https://www.nps.gov/ever/", schemaType: "TouristAttraction" },
  { name: "I-75 (Alligator Alley)", distance: "~8 miles", icon: "ri-road-map-line", desc: "Major interstate corridor connecting Naples to Fort Lauderdale across the Everglades and north to Tampa", url: "https://en.wikipedia.org/wiki/Interstate_75_in_Florida", schemaType: "CivicStructure" },
  { name: "Naples Zoo at Caribbean Gardens", distance: "~5 miles", icon: "ri-ancient-gate-line", desc: "AZA-accredited zoo and historic tropical garden featuring wildlife from around the world", url: "https://www.napleszoo.org/", schemaType: "TouristAttraction" },
];

const cities = [
  { name: "Naples", zips: "34102" },
  { name: "Marco Island", zips: "34145" },
  { name: "Bonita Springs", zips: "34135" },
  { name: "Estero", zips: "33928" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home, yacht club, or office in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function NaplesLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white pt-6 md:pt-8 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Naples &amp; Southwest Florida
          </h2>
        </div>

        {/* 300–500 word intro block */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Naples, Florida is not a typical travel market. This is a community where last-minute villa rentals on the Amalfi Coast, spontaneous yacht charters through the Greek Isles, and annual snowbird migrations to European capitals are woven into the lifestyle. Residents here do not plan vacations six months in advance the way most Americans do — they book when the mood strikes, when the market dips, or when a business opportunity overseas demands immediate presence. That travel culture creates a very specific passport challenge: when a Naples resident discovers an expired or damaged passport, the timeline is almost always shorter than standard government processing allows.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The seasonal resident dynamic adds another layer of urgency. Many Naples homeowners spend winters here and summers abroad, bouncing between homes in Italy, France, or the UK. It is remarkably common for a seasonal resident to arrive in November, pull out their passport in preparation for a December holiday trip, and realize it expired sometime during the summer. Standard post office processing, which currently stretches well beyond three months, simply does not accommodate that reality. The same applies to retirees who decide on a whim to join a Mediterranean cruise, or executives who need to close a deal in London with ten days notice.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Luxury travel in Naples also operates on compressed timelines. A destination wedding in the south of France, a charter flight to a private island, or a spur-of-the-moment booking at a five-star resort in the Maldives — these are not hypothetical scenarios here. They are the norm. And because affluent travelers expect concierge-level service in every other aspect of their lives, they rightfully expect the same from passport processing. Waiting in a government line, filling out forms without guidance, and hoping nothing gets rejected is not an acceptable standard for this community.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Fast Passport Center was built for exactly this type of traveler. We provide the white-glove, expedited passport service that Naples residents and seasonal homeowners expect: direct communication with a dedicated agent, professional document review before anything is submitted, and processing as fast as 24 hours for true emergencies. No lines. No guesswork. Just the same level of discretion and efficiency you would expect from any premium service in Southwest Florida.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Info + How It Works */}
          <div>
            {/* FPC Info Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex flex-col xl:flex-row gap-6">
                {/* Info side */}
                <div className="flex-1">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Naples, FL</p>
                  <p className="text-white/60 text-xs mt-0.5">1925 Metropolitan Blvd, Naples, FL 34112</p>
                  <a
                    href="https://maps.google.com/maps?daddr=1925+Metropolitan+Blvd,+Naples,+FL+34112"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#F6C344] hover:text-white text-xs font-semibold mt-1 transition-colors"
                  >
                    <i className="ri-navigation-line"></i> Get Driving Directions on Google Maps
                  </a>
                </div>
              </div>

              {/* Phone Number */}
              <div className="flex items-start gap-2.5 mb-3 ml-[52px]">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0 mt-0.5">
                  <i className="ri-phone-line text-[#F6C344] text-xs"></i>
                </div>
                <div>
                  <a href="tel:8772530084" className="text-white/80 text-sm hover:text-[#F6C344] transition-colors">(877) 253-0084</a>
                  <p className="text-white/40 text-xs">Nationwide toll-free — 24/7 voicemail</p>
                </div>
              </div>

              {/* Office Hours */}
              <div className="flex items-start gap-2.5 mb-4 ml-[52px]">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0 mt-0.5">
                  <i className="ri-time-line text-[#F6C344] text-xs"></i>
                </div>
                <div>
                  <div className="text-white/80 text-sm space-y-0.5">
                    <p className="whitespace-nowrap">Monday – Friday: 9:00 AM – 6:00 PM EST</p>
                    <p className="whitespace-nowrap">Saturday: 10:00 AM – 2:00 PM EST</p>
                    <p className="whitespace-nowrap">Sunday: Closed</p>
                  </div>
                  <p className="text-[#F6C344] text-xs mt-1.5 font-medium">Appointment suggested</p>
                  <p className="text-white/40 text-xs mt-0.5">Appointment suggested to ensure your documents are reviewed before you arrive.</p>
                  <p className="text-white/40 text-xs mt-2">Last updated: July 18, 2026</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Local office — serving Naples, Marco Island &amp; Southwest Florida
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Collier County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Naples office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

                {/* Map side */}
                <div className="xl:w-[340px] flex-shrink-0">
                  <div className="rounded-lg overflow-hidden border border-white/10" style={{ height: '260px' }}>
                    <iframe
                      src="https://maps.google.com/maps?q=1925+Metropolitan+Blvd,+Naples,+FL+34112&t=&z=15&ie=UTF8&iwloc=&output=embed"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Fast Passport Center Naples Office Location"
                      aria-label="Google Map showing Fast Passport Center Naples office at 1925 Metropolitan Blvd"
                    />
                  </div>
                  <p className="text-white/40 text-xs mt-2 text-center">
                    1925 Metropolitan Blvd, Naples, FL 34112
                  </p>
                </div>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Southwest Florida — start online, ship overnight, and we handle the rest. No need to drive to Miami or Fort Lauderdale.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Fort Myers", href: "/fort-myers" },
                    { name: "Marco Island", href: "/marco-island" },
                    { name: "Bonita Springs", href: "/bonita-springs" },
                    { name: "Estero", href: "/estero" },
                    { name: "Sarasota", href: "/sarasota" },
                    { name: "Cape Coral", href: "/cape-coral" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Naples Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Nearby Landmarks & Airports */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#F6C344]/15">
                  <i className="ri-landscape-line text-[#1A365D] text-xs"></i>
                </div>
                <h3 className="font-semibold text-[#1A365D] text-sm uppercase tracking-wide">Nearby Landmarks &amp; Airports</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {landmarks.map((item) => (
                  <span
                    key={item.name}
                    className="inline-flex items-center gap-1.5 bg-[#F8F9FB] border border-gray-200/80 rounded-full px-3 py-1.5 hover:border-[#F6C344] transition-colors cursor-default whitespace-nowrap"
                    title={item.desc}
                  >
                    <i className={`${item.icon} text-[#1A365D] text-[10px]`}></i>
                    <span className="text-[#1A365D] font-medium text-[11px]">{item.name}</span>
                    <span className="text-[#B22222] font-semibold text-[10px]">{item.distance}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}